import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { addDays, subDays, format, subMonths, isAfter } from "date-fns";
import { useEmailAnalytics } from "@/hooks/useEmailAnalytics";
import { BarChart, LineChart, PieChart } from "@/components/ui/chart";
import { useSubscriberGrowth } from "@/hooks/useNewsletterStats";
import { useCampaigns } from "@/hooks/useCampaigns";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download, BarChart3, TrendingUp, PieChart as PieChartIcon, Activity, MailOpen, MousePointerClick, Users } from "lucide-react";
import { EmailCampaignSummary } from "./analytics/EmailCampaignSummary";
import { SubscriberGrowthChart } from "./analytics/SubscriberGrowthChart";
import { EmailEngagementMetrics } from "./analytics/EmailEngagementMetrics";
import { ClickAnalysisChart } from "./analytics/ClickAnalysisChart";

interface NewsletterAnalyticsProps {
  initialCampaignId?: string;
  focusDetailed?: boolean;
}

export function NewsletterAnalytics({ initialCampaignId, focusDetailed = false }: NewsletterAnalyticsProps) {
  const [dateRange, setDateRange] = useState({
    from: subMonths(new Date(), 3),
    to: new Date(),
  });
  
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(initialCampaignId || null);
  const [activeTab, setActiveTab] = useState(focusDetailed ? "campaigns" : "overview");
  const [timeFrame, setTimeFrame] = useState<'day' | 'week' | 'month'>('day');
  
  // Effect to update selected campaign when initialCampaignId changes
  useEffect(() => {
    if (initialCampaignId) {
      setSelectedCampaignId(initialCampaignId);
    }
  }, [initialCampaignId]);
  
  // Effect to update active tab when focusDetailed changes
  useEffect(() => {
    if (focusDetailed) {
      setActiveTab("campaigns");
    }
  }, [focusDetailed]);
  
  const { campaigns, isLoading: campaignsLoading } = useCampaigns();
  const { useDeliveryStats, useOpenRateOverTime, useClickData } = useEmailAnalytics({
    campaignId: selectedCampaignId || undefined,
    startDate: dateRange.from.toISOString(),
    endDate: dateRange.to.toISOString(),
  });
  
  const { data: deliveryStats, isLoading: statsLoading } = useDeliveryStats();
  const { data: openRateData, isLoading: openRateLoading } = useOpenRateOverTime(timeFrame);
  const { data: clickData, isLoading: clickDataLoading } = useClickData();
  
  const { data: subscriberGrowth, isLoading: subscriberGrowthLoading } = useSubscriberGrowth(
    dateRange.from,
    dateRange.to
  );
  
  // Handle date range change
  const handleDateRangeChange = (range: { from: Date; to: Date }) => {
    setDateRange(range);
  };
  
  // Predefined date ranges
  const setLast7Days = () => {
    setDateRange({
      from: subDays(new Date(), 7),
      to: new Date(),
    });
  };
  
  const setLast30Days = () => {
    setDateRange({
      from: subDays(new Date(), 30),
      to: new Date(),
    });
  };
  
  const setLast3Months = () => {
    setDateRange({
      from: subMonths(new Date(), 3),
      to: new Date(),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Email Analytics</h2>
          <p className="text-slate-600">Track performance and engagement metrics for your email campaigns</p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="border-slate-200"
            onClick={setLast7Days}
          >
            Last 7 days
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="border-slate-200"
            onClick={setLast30Days}
          >
            Last 30 days
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="border-slate-200"
            onClick={setLast3Months}
          >
            Last 3 months
          </Button>
          <DateRangePicker
            value={dateRange}
            onValueChange={handleDateRangeChange}
          />
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="subscribers">Subscribers</TabsTrigger>
          <TabsTrigger value="links">Link Analysis</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <EmailEngagementMetrics deliveryStats={deliveryStats} isLoading={statsLoading} />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-medium">Open Rate Over Time</CardTitle>
                <CardDescription>Tracking email opens across time periods</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex justify-end mb-2">
                  <Select value={timeFrame} onValueChange={(value: 'day' | 'week' | 'month') => setTimeFrame(value)}>
                    <SelectTrigger className="w-[120px] h-8">
                      <SelectValue placeholder="Time Frame" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="day">Daily</SelectItem>
                      <SelectItem value="week">Weekly</SelectItem>
                      <SelectItem value="month">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {openRateLoading ? (
                  <div className="w-full h-64 flex items-center justify-center">
                    <Skeleton className="w-full h-64" />
                  </div>
                ) : (
                  <div className="w-full h-64">
                    <LineChart 
                      data={{
                        labels: openRateData?.map(item => item.date) || [],
                        datasets: [
                          {
                            label: 'Open Rate %',
                            data: openRateData?.map(item => item.open_rate) || [],
                            borderColor: 'rgb(99, 102, 241)',
                            backgroundColor: 'rgba(99, 102, 241, 0.1)',
                            tension: 0.2,
                            fill: true,
                          },
                          {
                            label: 'Click Rate %',
                            data: openRateData?.map(item => item.click_rate) || [],
                            borderColor: 'rgb(14, 165, 233)',
                            backgroundColor: 'rgba(14, 165, 233, 0.1)',
                            tension: 0.2,
                            fill: true,
                          }
                        ]
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
            
            <SubscriberGrowthChart 
              subscriberGrowth={subscriberGrowth || []} 
              isLoading={subscriberGrowthLoading} 
            />
          </div>
          
          <ClickAnalysisChart clickData={clickData || []} isLoading={clickDataLoading} />
        </TabsContent>
        
        <TabsContent value="campaigns" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Campaign Performance</CardTitle>
              <CardDescription>Compare metrics across different campaigns</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Select value={selectedCampaignId || ""} onValueChange={setSelectedCampaignId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select campaign" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">All Campaigns</SelectItem>
                    {campaignsLoading ? (
                      <SelectItem value="" disabled>Loading...</SelectItem>
                    ) : (
                      campaigns?.filter(c => c.status === 'sent')
                        .map(campaign => (
                          <SelectItem key={campaign.id} value={campaign.id}>
                            {campaign.name}
                          </SelectItem>
                        ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-4">
                <EmailCampaignSummary 
                  selectedCampaignId={selectedCampaignId} 
                  campaigns={campaigns || []} 
                  dateRange={dateRange}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="subscribers" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Subscriber Growth</CardTitle>
              <CardDescription>Track new subscribers and unsubscribes over time</CardDescription>
            </CardHeader>
            <CardContent className="h-[400px]">
              <SubscriberGrowthChart 
                subscriberGrowth={subscriberGrowth || []} 
                isLoading={subscriberGrowthLoading}
                height={350}
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="links" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Link Click Analysis</CardTitle>
              <CardDescription>See which links get the most engagement</CardDescription>
            </CardHeader>
            <CardContent className="h-[400px]">
              <ClickAnalysisChart 
                clickData={clickData || []} 
                isLoading={clickDataLoading}
                height={350}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
